import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import { emptyCv } from "@/lib/cv";
import TramTemplate from "@/components/TramTemplate";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SharePublic() {
  const { token } = useParams();
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/cv/public/${token}`)
      .then((r) => setCv({ ...emptyCv(), ...(r.data.data || {}) }))
      .catch((e) =>
        setError(
          e?.response?.status === 404
            ? "This share link is no longer active."
            : "Could not load this CV.",
        ),
      )
      .finally(() => setLoading(false));
  }, [token]);

  const download = async () => {
    if (!previewRef.current) return;
    setExporting(true);
    const el = previewRef.current;
    const prev = el.style.transform;
    try {
      el.style.transform = "none";
      el.classList.add("pdf-mode");
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf()
        .set({
          margin: 0,
          filename: `${(cv.full_name || "TRAM_CV").replace(/\s+/g, "_")}_TRAM.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", windowWidth: 794 },
          jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait", hotfixes: ["px_scaling"] },
          pagebreak: { mode: ["css"] },
        })
        .from(el)
        .save();
      toast.success("PDF downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      el.style.transform = prev;
      el.classList.remove("pdf-mode");
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen tram-grain">
      <header className="border-b border-[#e2ddd3] bg-white/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0f1c3f] text-white flex items-center justify-center font-serif-display text-lg font-bold tracking-wider">
              TR
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-[19px] font-semibold text-[#0f1c3f] tracking-widest">
                TRAM
              </div>
              <div className="text-[9px] font-mono-tram tracking-[0.35em] text-[#7a6b5d] uppercase -mt-0.5">
                Recruitment · Est 2024
              </div>
            </div>
          </div>
          {cv && (
            <Button
              onClick={download}
              disabled={exporting}
              className="bg-[#0f1c3f] hover:bg-[#0a122a] text-white"
              data-testid="public-download-btn"
            >
              <Download size={14} className="mr-1.5" />
              {exporting ? "Exporting…" : "Download PDF"}
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 py-10">
        {loading && (
          <div className="flex items-center justify-center text-[#5a6578] py-20">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading CV…
          </div>
        )}
        {error && (
          <div className="bg-white border border-dashed border-[#cfc8ba] rounded-2xl p-16 text-center">
            <div className="font-serif-display text-2xl text-[#0f1c3f]">Link unavailable</div>
            <p className="text-sm text-[#5a6578] mt-1">{error}</p>
          </div>
        )}
        {cv && (
          <div
            ref={previewRef}
            className="cv-preview-scale-public"
            data-testid="public-share-preview"
          >
            <TramTemplate cv={cv} />
          </div>
        )}
      </main>
    </div>
  );
}
